import type {
  PerformanceKpiConfigItem,
  PerformanceKpiIndicatorItem,
  PerformanceKpiQueryCondition,
  PerformanceKpiUpsertInputParams,
  PerformanceKpiUpsertParams,
} from './types/kpi-config'

import type { PagingRequest } from '#/api/type'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'

const INDICATOR_NAME_MAP: Record<string, string> = {
  '1': '总广告消耗',
  '2': '新客消耗',
  '3': '广告利润',
  '4': '客户数(留存)',
}

function normalizeIndicators(indicators: PerformanceKpiIndicatorItem[]) {
  return (
    indicators?.map((item) => ({
      indicatorId: item.indicatorId ?? '',
      indicatorName:
        item.indicatorName ||
        INDICATOR_NAME_MAP[String(item.indicatorId)] ||
        String(item.indicatorId),
      targetValue: Number(item.targetValue ?? 0),
      weight: Number(item.weight ?? 0),
    })) || []
  )
}

function normalizeKpiItem(item: PerformanceKpiConfigItem) {
  const indicators = normalizeIndicators(item.indicators || [])

  return {
    id: item.id ?? 0,
    indicators,
    indicatorsValue: indicators.map((item) => item.indicatorName).join('/'),
    quarter: item.quarter,
    userOpenId: item.userOpenId,
  }
}

export const performanceKpiConfigApi = defineApi('operation', ({ prefix }) => {
  /**
   * KPI配置列表
   * POST /gobestads/operation/adsPerformance/kpi/query
   */
  function getKpiConfigPage(
    params: PagingRequest<PerformanceKpiQueryCondition>,
  ) {
    return requestClient
      .post(prefix('adsPerformance/kpi/query'), {
        condition: {
          quarter: params.condition.quarter,
          userOpenId: params.condition.userOpenId,
        },
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
      })
      .then((res) => ({
        list:
          res.list?.map((item: PerformanceKpiConfigItem) =>
            normalizeKpiItem(item),
          ) || [],
        pageIndex: res.pageIndex,
        pageSize: res.pageSize,
        total: res.total,
      }))
  }

  /**
   * 新增/编辑绩效配置
   * POST /gobestads/operation/adsPerformance/kpi/upsert
   */
  function upsertKpiConfig(params: PerformanceKpiUpsertInputParams) {
    const normalizedParams: PerformanceKpiUpsertParams = {
      id: params.mode === 'detail' ? (params.currentId ?? 0) : 0,
      indicators: params.indicators.map((item) => ({
        indicatorId: item.indicatorId ?? '',
        targetValue: Number(item.targetValue ?? 0),
        weight: Number(item.weight ?? 0),
      })),
      quarter: params.quarter,
      userOpenId: params.userOpenId,
    }

    return requestClient.post(prefix('adsPerformance/kpi/upsert'), {
      id: normalizedParams.id,
      indicators: normalizedParams.indicators,
      quarter: normalizedParams.quarter,
      userOpenId: normalizedParams.userOpenId,
    })
  }

  return {
    getKpiConfigPage,
    upsertKpiConfig,
  }
})
